import { memo, useCallback } from 'react';

import { type ItemProps } from '~/components/Gallery/types';
import { AssetMetaData, type AssetMetaDataProps } from '~/components/TableView/NameCell/AssetNameCell/AssetMetaData';
import {
  EditableTableTitle,
  type EditableTableTitleProps,
} from '~/components/TableView/NameCell/NameCellContent/EditableTableTitle';
import { useClipPermissions } from '~/hooks/useClipPermissions';
import { useUpdateAssetTitle } from '~/swr-hooks/clips/useUpdateAssetTitle';
import { canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';
import { PrivateTableAssetTitle } from '../NameCell/NameCellContent/PrivateTableAssetTitle';
import { useIsItemRenaming, useRenamingContext } from './TableViewItemRenamingContext';

export type PrivateAssetNameCellTitleProps = {
  onClick: ItemProps['onItemClick'];
  onCmdClick: ItemProps['onItemCmdClick'];
  onCommentsClick?: () => void;
  onVersionsClick?: () => void;
  title: string;
  id: string;
  assetId: string;
  pagesCount: number;
} & Pick<AssetMetaDataProps, 'assetVersionCount' | 'openCommentCount'>;

export const PrivateAssetNameCellTitle = memo(
  ({
    onClick,
    onCmdClick,
    onCommentsClick,
    onVersionsClick,
    title,
    id,
    assetId,
    assetVersionCount,
    openCommentCount,
    pagesCount,
  }: PrivateAssetNameCellTitleProps) => {
    const { updateAssetTitle } = useUpdateAssetTitle();
    const { clipPermissions } = useClipPermissions({
      clipId: id,
    });
    const canViewDiscussions = canViewAssetDiscussions(clipPermissions);
    const { setIsRenaming } = useRenamingContext();
    const isCurrentlyRenaming = useIsItemRenaming(id);

    const onRenameComplete = useCallback<EditableTableTitleProps['onRenameComplete']>(
      (newTitle) => {
        updateAssetTitle(id, newTitle, assetId);
        setIsRenaming(null);
      },
      [updateAssetTitle, id, assetId, setIsRenaming],
    );

    return (
      <div
        className="relative ml-3 flex w-full flex-col items-start text-left"
        style={{
          zIndex: isCurrentlyRenaming ? 9999 : undefined,
        }}
        data-disableselect="true"
        onDoubleClick={(e) => {
          if (isCurrentlyRenaming) e.stopPropagation();
        }}
        onMouseDown={(e) => {
          if (isCurrentlyRenaming) e.stopPropagation();
        }}
        draggable={isCurrentlyRenaming ? true : undefined}
        onDragStart={(e) => {
          if (isCurrentlyRenaming) e.preventDefault();
        }}
      >
        {isCurrentlyRenaming ? (
          <EditableTableTitle
            title={title}
            setIsRenaming={() => setIsRenaming(null)}
            onRenameComplete={onRenameComplete}
          />
        ) : (
          <PrivateTableAssetTitle title={title} onClick={onClick} onCmdClick={onCmdClick} />
        )}
        {!isCurrentlyRenaming && (
          <AssetMetaData
            assetVersionCount={assetVersionCount}
            onCommentsClick={onCommentsClick}
            onVersionsClick={onVersionsClick}
            openCommentCount={openCommentCount}
            shouldShowDiscussions={canViewDiscussions}
            shouldShowVersions={true}
            pagesCount={pagesCount}
          />
        )}
      </div>
    );
  },
);

PrivateAssetNameCellTitle.displayName = 'PrivateAssetNameCellTitle';
